import { useState, useEffect } from "react";
import { FolderKanban, CheckCircle2, Circle, Calendar } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/customer/projects`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setProjects(data?.projects ?? data ?? []);
      } else {
        setProjects([]);
      }
    } catch (error) {
      toast.error("Failed to load projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getProgress = (project) => {
    if (!project.deliverables || project.deliverables.length === 0) return 0;
    const completed = (project.deliverables || []).filter(d => d.completed).length;
    return Math.round((completed / project.deliverables.length) * 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return '#22C55E';
      case 'active': return '#D4AF37';
      case 'waiting': return '#F59E0B';
      default: return '#71717A';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-projects">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Your Projects</h1>
        <p className="text-[#A1A1AA] mt-1">Track progress on your active projects</p>
      </div>

      <div className="space-y-4">
        {(projects || []).map((project) => (
          <Card key={project.id} className="bg-[#121214] border-[#27272A]" data-testid={`project-${project.id}`}>
            <CardContent className="pt-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-[#FAFAFA]">{project.title}</h3>
                  <p className="text-sm text-[#71717A] capitalize">{project.project_type?.replace('_', ' ')}</p>
                </div>
                <span 
                  className="badge"
                  style={{ 
                    backgroundColor: `${getStatusColor(project.status)}20`,
                    color: getStatusColor(project.status)
                  }}
                >
                  {project.status}
                </span>
              </div>

              {/* Client Notes */}
              {project.client_visible_notes && (
                <div className="p-4 bg-[#1A1A1D] rounded-lg mb-4">
                  <p className="text-sm text-[#D4AF37] font-medium mb-1">Latest Update</p>
                  <p className="text-[#FAFAFA]">{project.client_visible_notes}</p>
                </div>
              )}

              {/* Progress */}
              {project.deliverables?.length > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Progress</span>
                    <span className="text-[#D4AF37] font-medium">{getProgress(project)}%</span>
                  </div>
                  <Progress value={getProgress(project)} className="h-2" />
                </div>
              )}

              {/* Deliverables */}
              {project.deliverables?.length > 0 && (
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-[#71717A]">Deliverables</p>
                  {(project.deliverables || []).map((d) => (
                    <div key={d.name || d.id} className="flex items-center gap-2">
                      {d.completed ? (
                        <CheckCircle2 size={16} className="text-[#22C55E]" />
                      ) : (
                        <Circle size={16} className="text-[#71717A]" />
                      )}
                      <span className={d.completed ? "text-[#71717A] line-through" : "text-[#FAFAFA]"}>
                        {d.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer Info */}
              <div className="flex items-center justify-between pt-4 border-t border-[#27272A]">
                <div className="flex items-center gap-4">
                  {project.deadline && (
                    <span className="text-sm text-[#A1A1AA] flex items-center gap-1">
                      <Calendar size={14} />
                      Due: {project.deadline}
                    </span>
                  )}
                </div>
                {(project.deposit > 0 || project.remaining_balance > 0) && (
                  <div className="text-right">
                    {project.deposit > 0 && (
                      <p className="text-xs text-[#22C55E]">Deposit: {formatCurrency(project.deposit)}</p>
                    )}
                    {project.remaining_balance > 0 && (
                      <p className="text-sm text-[#F59E0B] font-medium">Balance: {formatCurrency(project.remaining_balance)}</p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {(projects || []).length === 0 && (
          <Card className="bg-[#121214] border-[#27272A]">
            <CardContent className="py-12 text-center">
              <FolderKanban size={48} className="text-[#71717A] mx-auto mb-3" />
              <p className="text-[#FAFAFA] font-medium">No Projects Yet</p>
              <p className="text-[#71717A]">Your projects will appear here once started</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomerProjects;
